import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Checkbox,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  Stack,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import BusinessIcon from "@mui/icons-material/Business";
import PaletteIcon from "@mui/icons-material/Palette";
import { API_URL, API_UPLOAD_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const CodeCampusReport = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: "#fff",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const colorInputSx = {
    ...inputSx,
    minWidth: 120,
    "& input": {
      height: 34,
      cursor: "pointer",
      p: 0.5,
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    boxShadow: "none",
    "&:hover": { backgroundColor: THEME_ORANGE_2, boxShadow: "none" },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    backgroundColor: "#fff",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      backgroundColor: "rgba(240,120,40,0.10)",
      color: THEME_ORANGE,
    },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": {
      borderBottom: "1px solid rgba(25,118,210,0.10)",
      whiteSpace: "nowrap",
      py: 0.8,
    },
  });

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  const isColorField = (field) =>
    field === "PrimaryThemeColor" || field === "SecondaryThemeColor";

  const makeBlankRow = (cc = "") => ({
    CampusCode: cc,
    CampusName: "",
    CampusLogo: "",
    LandingBackgroundImage: "",
    LandingTitle: "",
    LandingSubtitle: "",
    PrimaryThemeColor: "#F07828",
    SecondaryThemeColor: "#1976D2",
    AcademicYearStartMonth: 6,
    AcademicYearStartDay: 1,
    IsActive: 0,
  });

  const editableFields = [
    "CampusName",
    "CampusLogo",
    "LandingBackgroundImage",
    "LandingTitle",
    "LandingSubtitle",
    "PrimaryThemeColor",
    "SecondaryThemeColor",
    "AcademicYearStartMonth",
    "AcademicYearStartDay",
  ];

  const [data, setData] = useState([]);
  const [newRow, setNewRow] = useState(makeBlankRow());
  const [editRowId, setEditRowId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [campusCode, setCampusCode] = useState("");
  const [isUniFlooSuperAdmin, setIsUniFlooSuperAdmin] = useState(false);
  const fileInputRef = useRef();
  const token = localStorage.getItem("auth");

  const downloadSampleExcel = () => {
    const headers = [
      "CampusCode",
      "CampusName",
      "CampusLogo",
      "LandingBackgroundImage",
      "LandingTitle",
      "LandingSubtitle",
      "PrimaryThemeColor",
      "SecondaryThemeColor",
      "AcademicYearStartMonth",
      "AcademicYearStartDay",
      "IsActive",
    ];

    const sampleRows = [
      {
        CampusCode: "KIET",
        CampusName: "KIET",
        CampusLogo: "KIET/Logo/KIET.jpg",
        LandingBackgroundImage: "KIET/Logo/KIET-BG.jpg",
        LandingTitle: "KIET Digital Campus",
        LandingSubtitle: "Powered by UniFLOO",
        PrimaryThemeColor: "#F07828",
        SecondaryThemeColor: "#1976D2",
        AcademicYearStartMonth: 6,
        AcademicYearStartDay: 1,
        IsActive: 1,
      },
    ];

    const ws = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(18, h.length + 2) }));

    const instructions = [
      ["Field / Rule", "Instruction"],
      ["Sheet Name", "Keep sheet name exactly as: CodeCampus"],
      ["CampusCode", "Required. Example: KIET"],
      ["CampusLogo", "Example: KIET/Logo/KIET.jpg"],
      ["LandingBackgroundImage", "Example: KIET/Logo/KIET-BG.jpg"],
      ["LandingTitle", "Example: KIET Digital Campus"],
      ["LandingSubtitle", "Example: Powered by UniFLOO"],
      ["PrimaryThemeColor", "Example: #F07828"],
      ["SecondaryThemeColor", "Example: #1976D2"],
      ["AcademicYearStartMonth", "Required. Example: 6 for June"],
      ["AcademicYearStartDay", "Required. Example: 1 for 1st day of month"],
      ["IsActive", "Use 1 for Active, 0 for Inactive"],
    ];

    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    ws2["!cols"] = [{ wch: 24 }, { wch: 90 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CodeCampus");
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `CodeCampus_Sample_${campusCode || "CAMPUS"}.xlsx`);
  };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/CodeCampus`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      throw new Error("Refresh failed");
    }
  };

  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const cc = String(payload?.campusCode || "").trim().toUpperCase();
        const roles = Array.isArray(payload?.roles)
          ? payload.roles
          : [payload?.roles].filter(Boolean);
        const privileged = roles.includes("SuperAdmin") && cc === "UNIFLOO";

        setCampusCode(cc);
        setIsUniFlooSuperAdmin(privileged);
        setNewRow(makeBlankRow(cc));
      } catch {
        setError("Failed to decode auth token");
      }
    }
  }, [token]);

  useEffect(() => {
    if (!campusCode) return;
    fetchData().catch((err) => setError(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode]);

  const handleInputChange = (e, id = null) => {
    const { name, type, checked, value } = e.target;
    const val = type === "checkbox" ? (checked ? 1 : 0) : value;

    if (id == null) {
      setNewRow((r) => ({ ...r, [name]: val }));
    } else {
      setData((d) => d.map((r) => (r.id === id ? { ...r, [name]: val } : r)));
    }
  };

  const handleLandingBgUpload = async ({ file, targetCampusCode, rowId = null }) => {
    if (!targetCampusCode) {
      setError("CampusCode is required before uploading background image.");
      return;
    }

    if (!file) return;

    const allowed = ["image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) {
      setError("Only JPEG and PNG images are allowed.");
      return;
    }

    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      setError("File must be ≤ 5 MB.");
      return;
    }

    const form = new FormData();
    form.append("CampusCode", targetCampusCode);
    form.append("landingBackgroundImage", file);

    try {
      const res = await axios.post(`${API_URL}/api/uploadCampusLandingBackground`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const uploadedPath = res.data?.filename || res.data?.LandingBackgroundImage;

      if (rowId) {
        setData((d) =>
          d.map((r) =>
            r.id === rowId ? { ...r, LandingBackgroundImage: uploadedPath } : r
          )
        );
      } else {
        setNewRow((r) => ({
          ...r,
          LandingBackgroundImage: uploadedPath,
        }));
      }

      await fetchData();
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to upload landing background image");
    }
  };

  const renderEditableInput = ({ name, value, onChange, placeholder, rowId = null, rowCampusCode = "" }) => {
    if (name === "LandingBackgroundImage") {
      const imageUrl = value ? `${API_UPLOAD_URL}/uploads/${value}` : "";
        
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt="Landing BG"
              sx={{
                width: 70,
                height: 42,
                borderRadius: 1,
                objectFit: "cover",
                border: `1px solid ${BORDER}`,
              }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              No image
            </Typography>
          )}
    
          <Button
            variant="outlined"
            component="label"
            size="small"
            sx={outlinedBtnSx}
            startIcon={<CloudUploadIcon />}
          >
            Upload
            <input
              hidden
              type="file"
              accept="image/jpeg,image/png"
              onChange={(e) => {
                const file = e.target.files?.[0];
                handleLandingBgUpload({
                  file,
                  targetCampusCode: rowCampusCode,
                  rowId,
                });
                e.target.value = "";
              }}
            />
          </Button>
        </Stack>
      );
    }
  
    if (isColorField(name)) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            name={name}
            value={value || (name === "PrimaryThemeColor" ? "#F07828" : "#1976D2")}
            onChange={onChange}
            fullWidth
            size="small"
            sx={colorInputSx}
            type="color"
          />
          <Typography variant="caption" sx={{ minWidth: 72, fontWeight: 700 }}>
            {value || (name === "PrimaryThemeColor" ? "#F07828" : "#1976D2")}
          </Typography>
        </Stack>
      );
    }
  
    return (
      <TextField
        name={name}
        value={value ?? ""}
        onChange={onChange}
        fullWidth
        size="small"
        sx={inputSx}
        placeholder={placeholder || name}
        disabled={name === "CampusLogo" || name === "LandingSubtitle"}
      />
    );
  };

  const validateCampusRow = (row) => {
    const month = Number(row.AcademicYearStartMonth);
    const day = Number(row.AcademicYearStartDay);

    if (!row.CampusCode?.trim()) return "CampusCode is required.";
    if (!row.CampusName?.trim()) return "CampusName is required.";

    if (!month || month < 1 || month > 12) {
      return "AcademicYearStartMonth must be between 1 and 12.";
    }

    if (!day || day < 1 || day > 31) {
      return "AcademicYearStartDay must be between 1 and 31.";
    }

    return "";
  };

  const handleAddRow = async () => {
    if (!isUniFlooSuperAdmin) {
      setError("Only UNIFLOO SuperAdmin can add campuses.");
      return;
    }

    const prevData = data;

    const validationError = validateCampusRow(newRow);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await axios.post(`${API_URL}/api/CodeCampus`, newRow, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess(true);
      await fetchData();
      setNewRow(makeBlankRow(campusCode));
    } catch (err) {
      setError(
        err.response?.status === 409
          ? err.response.data.error
          : err.message || "Failed to add row"
      );
      setData(prevData);
    }
  };

  const handleUpdateRow = async (id) => {
    const prevData = data;
    const updated = data.find((r) => r.id === id);

    const validationError = validateCampusRow(updated);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await axios.put(`${API_URL}/api/CodeCampus/${id}`, updated, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess(true);
      setEditRowId(null);
      await fetchData();
    } catch {
      setError("Failed to update row");
      setData(prevData);
    }
  };

  const handleDeleteRow = async (id) => {
    if (!isUniFlooSuperAdmin) {
      setError("Only UNIFLOO SuperAdmin can delete campuses.");
      return;
    }

    const prevData = data;

    try {
      await axios.delete(`${API_URL}/api/CodeCampus/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess(true);
      await fetchData();
    } catch (err) {
      setError(err.response?.status === 409 ? err.response.data.error : "Failed to delete row");
      setData(prevData);
    }
  };

  const handleBulkClick = () => {
    if (!isUniFlooSuperAdmin) {
      setError("Only UNIFLOO SuperAdmin can bulk upload campuses.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFile = async (e) => {
    if (!isUniFlooSuperAdmin) {
      setError("Only UNIFLOO SuperAdmin can bulk upload campuses.");
      e.target.value = "";
      return;
    }

    const prevData = data;
    const file = e.target.files[0];

    if (!file) return;

    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", campusCode);

    try {
      await axios.post(`${API_URL}/api/UploadCodeCampus`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      await fetchData();
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || "Bulk upload failed");
      setData(prevData);
    } finally {
      e.target.value = "";
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <BusinessIcon sx={{ fontSize: 34 }} />
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
                Upload Campus
              </Typography>
              <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
                Maintain campus master and landing branding
              </Typography>
            </Box>
          </Stack>

          {isUniFlooSuperAdmin && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                sx={outlinedBtnSx}
                startIcon={<DownloadIcon />}
                onClick={downloadSampleExcel}
              >
                Download Sample
              </Button>

              <Button
                variant="contained"
                sx={primaryBtnSx}
                startIcon={<UploadIcon />}
                onClick={handleBulkClick}
              >
                Bulk Upload
              </Button>

              <input
                type="file"
                accept=".xlsx,.xls"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFile}
              />
            </Box>
          )}
        </Box>

        <Box sx={bodySx}>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mt: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          <Paper elevation={0} sx={tableWrapSx}>
            <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
              <TableContainer component="div">
                <Table size="small" sx={{ minWidth: "max-content" }}>
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell>#</TableCell>
                      <TableCell>Campus</TableCell>
                      <TableCell>CampusName</TableCell>
                      <TableCell>CampusLogo</TableCell>
                      <TableCell>LandingBackgroundImage</TableCell>
                      <TableCell>LandingTitle</TableCell>
                      <TableCell>LandingSubtitle</TableCell>
                      <TableCell>PrimaryThemeColor</TableCell>
                      <TableCell>SecondaryThemeColor</TableCell>
                      <TableCell>AcademicYearStartMonth</TableCell>
                      <TableCell>AcademicYearStartDay</TableCell>
                      <TableCell>IsActive</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data.map((row, idx) => {
                      const isEdit = editRowId === row.id;

                      return isEdit ? (
                        <TableRow key={row.id} sx={zebraRowSx(idx)}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{row.CampusCode}</TableCell>

                          {editableFields.map((f) => (
                            <TableCell key={f}>
                              {renderEditableInput({
                                name: f,
                                value: row[f],
                                onChange: (e) => handleInputChange(e, row.id),
                                placeholder: f,
                                rowId: row.id,
                                rowCampusCode: row.CampusCode,
                              })}
                            </TableCell>
                          ))}

                          <TableCell>
                            <Checkbox
                              name="IsActive"
                              checked={!!row.IsActive}
                              onChange={(e) => handleInputChange(e, row.id)}
                              sx={checkboxSx}
                            />
                          </TableCell>

                          <TableCell>
                            <IconButton sx={iconBtnSx} onClick={() => handleUpdateRow(row.id)} title="Save">
                              <SaveIcon />
                            </IconButton>
                            <IconButton sx={iconBtnSx} onClick={() => setEditRowId(null)} title="Cancel">
                              <CancelIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow key={row.id} sx={zebraRowSx(idx)}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{row.CampusCode}</TableCell>
                          <TableCell>{row.CampusName}</TableCell>
                          <TableCell>{row.CampusLogo}</TableCell>
                          <TableCell>{row.LandingBackgroundImage}</TableCell>
                          <TableCell>{row.LandingTitle}</TableCell>
                          <TableCell>{row.LandingSubtitle}</TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <PaletteIcon sx={{ color: row.PrimaryThemeColor || THEME_ORANGE }} />
                              <Typography variant="body2">{row.PrimaryThemeColor}</Typography>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <PaletteIcon
                                sx={{
                                  color: row.SecondaryThemeColor || "rgba(25,118,210,0.95)",
                                }}
                              />
                              <Typography variant="body2">{row.SecondaryThemeColor}</Typography>
                            </Stack>
                          </TableCell>

                          <TableCell>{row.AcademicYearStartMonth}</TableCell>
                          <TableCell>{row.AcademicYearStartDay}</TableCell>

                          <TableCell>
                            <Checkbox checked={!!row.IsActive} readOnly sx={checkboxSx} />
                          </TableCell>

                          <TableCell>
                            <IconButton sx={iconBtnSx} onClick={() => setEditRowId(row.id)} title="Edit">
                              <EditIcon />
                            </IconButton>
                            {isUniFlooSuperAdmin && (
                              <IconButton sx={iconBtnSx} onClick={() => handleDeleteRow(row.id)} title="Delete">
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {isUniFlooSuperAdmin && (
                      <TableRow sx={zebraRowSx(data.length)}>
                      <TableCell />
                      <TableCell>
                        <TextField
                          name="CampusCode"
                          value={newRow.CampusCode}
                          onChange={handleInputChange}
                          placeholder="CampusCode"
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />
                      </TableCell>

                      {editableFields.map((name) => (
                        <TableCell key={name}>
                          {renderEditableInput({
                            name,
                            value: newRow[name],
                            onChange: handleInputChange,
                            placeholder: name,
                            rowCampusCode: newRow.CampusCode,
                          })}
                        </TableCell>
                      ))}

                      <TableCell>
                        <Checkbox
                          name="IsActive"
                          checked={!!newRow.IsActive}
                          onChange={(e) =>
                            setNewRow((r) => ({
                              ...r,
                              IsActive: e.target.checked ? 1 : 0,
                            }))
                          }
                          sx={checkboxSx}
                        />
                      </TableCell>

                      <TableCell>
                        <IconButton sx={iconBtnSx} onClick={handleAddRow} title="Add">
                          <AddIcon />
                        </IconButton>
                      </TableCell>
                      </TableRow>
                    )}

                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default CodeCampusReport;
